/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "API overview", description = "Hightouch exposes a REST style API that allows users to interact with resources like syncs, models, sources and destinations.", lastUpdatedDate = new Date(1749772800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "API overview",
        description: "Hightouch exposes a REST style API that allows users to interact with resources like syncs, models, sources and destinations.",
        lastUpdatedDate: "Fri Jun 13 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Required permissions",
          "depth": 0,
          "id": "required-permissions"
        }, {
          "value": "Create an API key",
          "depth": 0,
          "id": "create-an-api-key"
        }, {
          "value": "Revoke an API key",
          "depth": 0,
          "id": "revoke-an-api-key"
        }, {
          "value": "Hightouch API Rate Limits",
          "depth": 0,
          "id": "hightouch-api-rate-limits"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common Errors",
          "depth": 1,
          "id": "common-errors"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      ol: "ol",
      li: "li",
      strong: "strong",
      h3: "h3",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink, CreateAPIKeySnippet} = _components;
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch exposes a REST API that lets users interact with resources like syncs, models, sources, and destinations.\nThough the REST API is open to all Hightouch users, we aim to build features so you don't need to rely on it directly. For example, ", _jsx(_components.a, {
          href: "/extensions/git-sync",
          children: "Git Sync"
        }), " lets you manage workspace resources programmatically."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're looking to create a custom destination, it's best to use the ", _jsx(_components.a, {
          href: "/destinations/http-request",
          children: "HTTP Request destination"
        }), " as your base. If you find you still need to use the REST API directly,   ", _jsx(PylonLink, {
          pylonText: "",
          linkText: "we'd love to hear from you"
        }), " about your use case."]
      }), "\n", _jsx(_components.p, {
        children: "To start using the API, you must first create and retrieve an API key from your workspace."
      }), "\n", _jsx(_components.h2, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["To use Hightouch programmatically through the API, you must use an API key created by an Admin user of your Hightouch workspace. See ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/workspace-management/rbac#default-roles",
          children: "Role Based Access"
        }), " for more information."]
      }), "\n", _jsx(_components.h2, {
        id: "create-an-api-key",
        children: "Create an API key"
      }), "\n", _jsx(_components.p, {
        children: "Log in to Hightouch as an Admin user of your Hightouch workspace, then follow these steps:"
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsxs(_components.p, {
        children: ["Once you have an API key, you can follow the ", _jsx(_components.a, {
          href: "/api-reference",
          children: "API Reference documentation"
        }), " to start using the API."]
      }), "\n", _jsx(_components.h2, {
        id: "revoke-an-api-key",
        children: "Revoke an API key"
      }), "\n", _jsx(_components.p, {
        children: "Your API key provides read/write access to sensitive Hightouch resources and should be kept secure. If you believe your API key may have been compromised, revoke it immediately."
      }), "\n", _jsx(_components.p, {
        children: "Log in to Hightouch as an Admin user of your Hightouch workspace, then follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/api-keys",
            children: [_jsx(_components.strong, {
              children: "API keys"
            }), " tab"]
          }), " on the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page, select the API key or keys you want to revoke."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Delete selected"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the modal that appears, select ", _jsx(_components.strong, {
            children: "Delete"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "hightouch-api-rate-limits",
        children: "Hightouch API Rate Limits"
      }), "\n", _jsx(_components.p, {
        children: "The Hightouch API rate limit is 200 requests per 10 seconds per workspace."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsxs(_components.p, {
        children: ["If you encounter issues using the Hightouch API, check our ", _jsx(_components.a, {
          href: "https://status.hightouch.io/",
          children: "status page"
        }), " or ", _jsx(PylonLink, {
          pylonText: "Hi, I'm having trouble with the API.",
          linkText: "contact us"
        }), " for assistance."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common Errors"
      }), "\n", _jsx(_components.p, {
        children: "The following error indicates an issue with your API key. Check that the creator of the API key is an Admin user of your Hightouch workspace."
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Authentication error\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"details\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"No user found\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
